import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import { Card, Skeleton } from 'antd';
import { ProTable, DateDisplay } from 'components/layout';
import {
  renderProTableSearchProjectCategory,
  renderProTableSearchFaculty,
  renderProTableSearchDepartment,
  renderProTableSearchProjectStatus,
  renderProTableSearchTeamMember,
  renderProTableSearchFundingSource,
} from 'components/layout/ProTable';
import { GET_PROJECTS } from 'gql/projects';
import { useQueryParams } from 'hooks';
import { ENUM_SORT_ORDER } from 'constants/form';

const defaultPageSize = 50;
const ProjectsTable = ({ canViewAll, actionRef }) => {
  const initial = useRef(true);
  const status = canViewAll ? 'ALL' : 'ACTIVE';

  const formRef = useRef();
  let queryParams = useQueryParams();
  const query = {
    department: queryParams.get('department'),
    faculty: queryParams.get('faculty'),
    teamMember: queryParams.get('teamMember'),
  };

  const { data, loading, refetch } = useQuery(GET_PROJECTS, {
    variables: {
      status,
      current: 1,
      pageSize: defaultPageSize,
      projectStatus: null,
      departments: query.department ? [query.department] : null,
      faculties: query.faculty ? [query.faculty] : null,
      teamMember: query.teamMember,
    },
  });
  const defaultCollapsed =
    query.department || query.faculty || query.teamMember ? false : true;

  const columns = [
    {
      title: 'Project Category',
      key: 'projectCategory',
      colSize: 2,
      sorter: true,
      dataIndex: 'projectCategory',
      render: (_, { projectCategory, id }) => (
        <Link to={`/projects/${id}/profile`}>{projectCategory.name}</Link>
      ),
      renderFormItem: renderProTableSearchProjectCategory,
    },
    {
      title: 'Project Title / Code / Work Prog.',
      key: 'keyword',
      hideInTable: true,
      colSize: 1,
    },
    {
      title: 'Faculty',
      key: 'faculty',
      hideInTable: true,
      renderFormItem: renderProTableSearchFaculty,
      initialValue: query.faculty ?? undefined,
    },
    {
      title: 'Department',
      key: 'department',
      sorter: true,
      dataIndex: 'department',
      render: (_, { profile, id }) => (
        <Link to={`/projects/${id}/profile`}>
          {profile ? profile.department.abbreviation : ' - '}
        </Link>
      ),
      renderFormItem: renderProTableSearchDepartment,
      initialValue: query.department ?? undefined,
    },
    {
      title: 'Project Code',
      key: 'projectCode',
      hideInSearch: true,
      sorter: true,
      dataIndex: 'projectCode',
      render: (_, { profile, id }) => (
        <Link to={`/projects/${id}/profile`}>
          {profile ? profile.projectCode : ' - '}
        </Link>
      ),
    },
    {
      title: 'Project Title',
      key: 'title',
      sorter: true,
      dataIndex: 'title',
      render: (_, { title, id }) => (
        <Link to={`/projects/${id}/profile`}>{title}</Link>
      ),
      hideInSearch: true,
    },
    {
      title: 'Work Prog.',
      key: 'workProgramme',
      hideInSearch: true,
      sorter: true,
      dataIndex: 'workProgramme',
      render: (_, { profile, id }) => (
        <Link to={`/projects/${id}/profile`}>
          {profile ? profile.workProgramme : ' - '}
        </Link>
      ),
    },
    {
      title: 'Project Duration',
      key: 'projectDuration',
      hideInSearch: true,
      sorter: true,
      dataIndex: 'projectDuration',
      render: (_, { profile, id }) => (
        <Link to={`/projects/${id}/profile`}>
          {profile ? (
            <>
              <DateDisplay
                date={profile.originalStartDate}
                format="DD/MM/YYYY"
                nullValue=" "
              />
              {' - '}
              <DateDisplay
                date={
                  profile.lastApprovedCompletionDate ??
                  profile.originalCompletionDate
                }
                format="DD/MM/YYYY"
                nullValue=" "
              />
            </>
          ) : (
            ' - '
          )}
        </Link>
      ),
    },
    {
      title: 'Project Duration',
      key: 'searchProjectDuration',
      valueType: 'dateRange',
      hideInTable: true,
      search: {
        transform: (value) => {
          return {
            projectDuration: {
              startDate: value[0],
              endDate: value[1],
            },
          };
        },
      },
    },
    {
      title: 'Approved Funding Source',
      key: 'approvedFundingSource',
      hideInTable: true,
      renderFormItem: renderProTableSearchFundingSource,
    },
    {
      title: 'Additional Funding Source',
      key: 'additionalFundingSource',
      hideInTable: true,
      renderFormItem: renderProTableSearchFundingSource,
    },
    {
      title: 'Project Status',
      key: 'projectStatus',
      dataIndex: 'projectStatus',
      sorter: true,
      render: (_, { projectStatus, id }) => (
        <Link to={`/projects/${id}/profile`}>
          {projectStatus ? projectStatus.name : ' - '}
        </Link>
      ),
      renderFormItem: renderProTableSearchProjectStatus,
    },
    {
      title: 'Team Member',
      key: 'teamMember',
      hideInTable: true,
      renderFormItem: renderProTableSearchTeamMember,
      initialValue: query.teamMember ?? undefined,
    },
  ];

  return (
    <>
      {loading ? (
        <Card>
          <Skeleton active />
        </Card>
      ) : (
        <ProTable
          actionRef={actionRef}
          columns={columns}
          dataSource={data?.projects?.nodes}
          request={async (params, sort, _filter) => {
            try {
              if (initial.current) {
                initial.current = false;
                return;
              }
              const {
                current,
                pageSize,
                department,
                faculty,
                keyword,
                projectCategory,
                projectDuration,
                projectStatus,
                teamMember,
                approvedFundingSource,
                additionalFundingSource,
              } = params;
              const result = await refetch({
                status,
                current,
                pageSize,
                projectStatus: projectStatus ? [projectStatus] : null,
                departments: department ? [department] : null,
                faculties: faculty ? [faculty] : null,
                keyword,
                teamMember,
                projectCategories: projectCategory ? [projectCategory] : null,
                projectDuration,
                approvedFundingSource,
                additionalFundingSource,
                sort: {
                  by: Object.keys(sort)[0],
                  order: ENUM_SORT_ORDER[sort[Object.keys(sort)[0]]],
                },
              });
              return {
                data: result.data?.projects?.nodes,
                success: true,
                total: result.data?.projects?.totalCount,
              };
            } catch (error) {
              console.log(error);
              return {
                data: [],
                success: false,
              };
            }
          }}
          formRef={formRef}
          options={{
            density: false,
          }}
          search={{
            defaultCollapsed,
            span: {
              xs: 24,
              sm: 24,
              md: 24,
              lg: 12,
              xl: 8,
              xxl: 8,
            },
            labelWidth: 'auto',
            searchText: 'Search',
          }}
          pagination={{
            defaultPageSize,
            showLessItems: true,
            total: data?.projects?.totalCount,
          }}
          size="middle"
          rowKey="id"
          onReset={() => {
            if (formRef.current) {
              formRef.current.setFieldsValue({
                faculty: null,
                department: null,
                teamMember: null,
              });
              formRef.current.submit();
            }
          }}
        />
      )}
    </>
  );
};

ProjectsTable.defaultProps = {
  canViewAll: false,
};

ProjectsTable.propTypes = {
  canViewAll: PropTypes.bool.isRequired,
};

export default ProjectsTable;
